import { useState } from "react";
import { sendNoticeEmail } from "../utils/emailService";
import { uploadNoticeToR2 } from "../utils/noticeStorage";
import S from "../styles/styles";

// ── BC Rent Increase Rules ─────────────────────────────────────────────────
const BC_RATES = {
  2022: 1.5, 2023: 2.0, 2024: 3.5, 2025: 3.0, 2026: 3.0,
};
const CURRENT_YEAR = new Date().getFullYear();
const MAX_RATE     = BC_RATES[CURRENT_YEAR] ?? 3.0;

// ── Helpers ────────────────────────────────────────────────────────────────
const fmt  = (n) => parseFloat(n || 0).toFixed(2);
const fmtC = (n) => `$${parseFloat(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().split("T")[0];

// Add 3 full months + 1 day for RTB notice (must be first of month after 3 months)
const calcEffectiveDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  d.setDate(1);
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().split("T")[0];
};

const formatDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}`;
};

// ── RTB-7 PDF Generator ────────────────────────────────────────────────────
const generateRTB7 = (notice) => {
  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>RTB-7 Notice of Rent Increase</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 13px; color: #000; margin: 0; padding: 40px; }
  .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 16px; margin-bottom: 24px; }
  .header h1 { font-size: 18px; margin: 0 0 4px; }
  .header h2 { font-size: 14px; font-weight: normal; margin: 0; }
  .form-no { font-size: 12px; color: #444; margin-top: 6px; }
  .section { margin-bottom: 20px; }
  .section-title { font-weight: bold; font-size: 13px; border-bottom: 1px solid #ccc; padding-bottom: 4px; margin-bottom: 12px; }
  .row { display: flex; gap: 24px; margin-bottom: 10px; }
  .field { flex: 1; }
  .field label { font-size: 11px; color: #555; display: block; margin-bottom: 2px; }
  .field .val { border-bottom: 1px solid #000; padding: 4px 2px; min-height: 22px; font-size: 13px; }
  .highlight { background: #f5f5f5; border: 1px solid #ccc; border-radius: 4px; padding: 12px 16px; margin: 16px 0; }
  .highlight .big { font-size: 22px; font-weight: bold; }
  .legal { font-size: 11px; color: #444; line-height: 1.6; margin-top: 20px; border-top: 1px solid #ccc; padding-top: 12px; }
  .sig-row { display: flex; gap: 40px; margin-top: 32px; }
  .sig-block { flex: 1; border-top: 1px solid #000; padding-top: 6px; font-size: 11px; }
  .watermark { text-align: center; font-size: 11px; color: #aaa; margin-top: 40px; }
  @media print { body { padding: 20px; } }
</style>
</head>
<body>
<div class="header">
  <h1>NOTICE OF RENT INCREASE</h1>
  <h2>Residential Tenancy Act — British Columbia</h2>
  <div class="form-no">Form RTB-7 &nbsp;|&nbsp; Generated by PropDesk &nbsp;|&nbsp; ${new Date().toLocaleDateString("en-CA")}</div>
</div>

<div class="section">
  <div class="section-title">Rental Unit Information</div>
  <div class="row">
    <div class="field"><label>Tenant Name(s)</label><div class="val">${notice.tenantName}</div></div>
    <div class="field"><label>Unit Number</label><div class="val">${notice.unit}</div></div>
  </div>
  <div class="row">
    <div class="field"><label>Civic Address</label><div class="val">${notice.address}</div></div>
    <div class="field"><label>City</label><div class="val">${notice.city || "Vancouver, BC"}</div></div>
  </div>
</div>

<div class="section">
  <div class="section-title">Landlord / Property Manager Information</div>
  <div class="row">
    <div class="field"><label>Landlord / Agent Name</label><div class="val">${notice.landlordName}</div></div>
    <div class="field"><label>Phone</label><div class="val">${notice.landlordPhone || ""}</div></div>
  </div>
  <div class="row">
    <div class="field"><label>Mailing Address</label><div class="val">${notice.landlordAddress || ""}</div></div>
    <div class="field"><label>Email</label><div class="val">${notice.landlordEmail || ""}</div></div>
  </div>
</div>

<div class="section">
  <div class="section-title">Rent Increase Details</div>
  <div class="row">
    <div class="field"><label>Current Monthly Rent</label><div class="val">${fmtC(notice.currentRent)}</div></div>
    <div class="field"><label>Increase Amount</label><div class="val">${fmtC(notice.increaseAmount)}</div></div>
    <div class="field"><label>New Monthly Rent</label><div class="val">${fmtC(notice.newRent)}</div></div>
  </div>
  <div class="row">
    <div class="field"><label>Percentage Increase</label><div class="val">${fmt(notice.increasePercent)}% (BC maximum ${MAX_RATE}% for ${CURRENT_YEAR})</div></div>
    <div class="field"><label>Date Notice Served</label><div class="val">${formatDate(notice.noticeDate)}</div></div>
    <div class="field"><label>Effective Date of Increase</label><div class="val">${formatDate(notice.effectiveDate)}</div></div>
  </div>

  <div class="highlight">
    <div>New Monthly Rent Effective <strong>${formatDate(notice.effectiveDate)}</strong></div>
    <div class="big">${fmtC(notice.newRent)} / month</div>
  </div>
</div>

<div class="legal">
  <strong>Important Information for Tenants:</strong><br/>
  • Under the Residential Tenancy Act, a landlord may increase rent once every 12 months with proper notice.<br/>
  • This notice must be given at least 3 full months before the effective date of the increase.<br/>
  • The maximum allowable rent increase for ${CURRENT_YEAR} is <strong>${MAX_RATE}%</strong> as set by the Province of BC.<br/>
  • If you believe this increase exceeds the allowable amount, you may apply for dispute resolution through the Residential Tenancy Branch.<br/>
  • Residential Tenancy Branch: <strong>1-800-665-8779</strong> &nbsp;|&nbsp; www.gov.bc.ca/landlordtenant
</div>

<div class="sig-row">
  <div class="sig-block">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>
  <div class="sig-block">Tenant Acknowledgement Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>
</div>

<div class="watermark">Generated by PropDesk · propdesk.ca · Property Management Software</div>
</body>
</html>`;

  const sigHtml = notice.sigName
    ? '<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap" rel="stylesheet"/>'
      + '<div style="margin-top:24px"><div style="font-size:11px;color:#555;margin-bottom:4px">Landlord / Agent Signature</div>'
      + '<div style="font-family:Dancing Script,Brush Script MT,cursive;font-size:28px;color:#000;border-bottom:1px solid #000;padding-bottom:4px;display:inline-block;min-width:200px">' + notice.sigName + '</div>'
      + '<div style="font-size:11px;color:#555;margin-top:4px">' + new Date().toLocaleDateString("en-CA") + '</div></div>'
    : '<div style="margin-top:24px;border-top:1px solid #000;padding-top:6px;font-size:11px">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>';

  const finalHtml = html.replace('<div class="sig-row">', sigHtml + '\n<div class="sig-row" style="margin-top:20px">');

  const blob = new Blob([finalHtml], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `RTB-7_${notice.tenantName.replace(/\s+/g,"_")}_${notice.effectiveDate}.html`;
  a.click();
  URL.revokeObjectURL(url);
  return finalHtml;
};


// ── RTB-32 PDF Generator ───────────────────────────────────────────────────
const generateRTB32 = (n) => {
  const reasonLabels = [
    "Landlord's use of property (landlord, close family member, or authorized occupant will occupy the unit)",
    "Sale of the property (buyer or buyer's family member will occupy the unit)",
    "Conversion to a non-rental use (strata conversion, demolition permit, or rezoning)",
    "Other reason permitted under Section 49 of the Residential Tenancy Act",
  ];
  const reasonBoxes = reasonLabels.map((r, i) =>
    `<div class="reason-box"><span class="checkbox${n.reasonIndex === i ? " checked" : ""}"></span>${r}</div>`
  ).join("");

  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
    '<title>RTB-32 Two Month Notice to End Tenancy</title>',
    '<style>',
    'body{font-family:Arial,sans-serif;font-size:13px;color:#000;margin:0;padding:40px}',
    '.header{text-align:center;border-bottom:2px solid #000;padding-bottom:16px;margin-bottom:24px}',
    '.header h1{font-size:18px;margin:0 0 4px}.header h2{font-size:14px;font-weight:normal;margin:0}',
    '.form-no{font-size:12px;color:#444;margin-top:6px}',
    '.section{margin-bottom:20px}.section-title{font-weight:bold;font-size:13px;border-bottom:1px solid #ccc;padding-bottom:4px;margin-bottom:12px}',
    '.row{display:flex;gap:24px;margin-bottom:10px}.field{flex:1}',
    '.field label{font-size:11px;color:#555;display:block;margin-bottom:2px}',
    '.field .val{border-bottom:1px solid #000;padding:4px 2px;min-height:22px;font-size:13px}',
    '.reason-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin:8px 0;background:#fafafa}',
    '.highlight{background:#f5f5f5;border:1px solid #ccc;border-radius:4px;padding:12px 16px;margin:16px 0}',
    '.legal{font-size:11px;color:#444;line-height:1.6;margin-top:20px;border-top:1px solid #ccc;padding-top:12px}',
    '.sig-row{display:flex;gap:40px;margin-top:32px}.sig-block{flex:1;border-top:1px solid #000;padding-top:6px;font-size:11px}',
    '.watermark{text-align:center;font-size:11px;color:#aaa;margin-top:40px}',
    '.checkbox{display:inline-block;width:14px;height:14px;border:1px solid #000;margin-right:8px;vertical-align:middle}',
    '.checked{background:#000}',
    '@media print{body{padding:20px}}',
    '</style></head><body>',
    '<div class="header"><h1>TWO MONTH NOTICE TO END TENANCY</h1>',
    '<h2>Residential Tenancy Act — British Columbia — Section 49</h2>',
    '<div class="form-no">Form RTB-32 &nbsp;|&nbsp; Generated by PropDesk &nbsp;|&nbsp; ' + new Date().toLocaleDateString("en-CA") + '</div></div>',
    '<div class="section"><div class="section-title">Rental Unit Information</div>',
    '<div class="row"><div class="field"><label>Tenant Name(s)</label><div class="val">' + (n.tenantName||"") + '</div></div>',
    '<div class="field"><label>Unit Number</label><div class="val">' + (n.unit||"") + '</div></div></div>',
    '<div class="row"><div class="field"><label>Civic Address</label><div class="val">' + (n.address||"") + '</div></div>',
    '<div class="field"><label>City / Province</label><div class="val">' + (n.city||"Vancouver, BC") + '</div></div></div></div>',
    '<div class="section"><div class="section-title">Landlord / Property Manager</div>',
    '<div class="row"><div class="field"><label>Name</label><div class="val">' + (n.landlordName||"") + '</div></div>',
    '<div class="field"><label>Phone</label><div class="val">' + (n.landlordPhone||"") + '</div></div></div>',
    '<div class="row"><div class="field"><label>Mailing Address</label><div class="val">' + (n.landlordAddress||"") + '</div></div>',
    '<div class="field"><label>Email</label><div class="val">' + (n.landlordEmail||"") + '</div></div></div></div>',
    '<div class="section"><div class="section-title">Reason for Notice</div>',
    reasonBoxes,
    (n.reasonIndex === 3 ? '<div class="row"><div class="field"><label>Describe reason</label><div class="val">' + (n.reasonDetail||"") + '</div></div></div>' : ''),
    '</div>',
    '<div class="section"><div class="section-title">Notice Details</div>',
    '<div class="highlight">Tenant must vacate by <strong>' + formatDate(n.vacateDate) + '</strong><br/>',
    '<span style="font-size:11px;color:#555">At least two full calendar months from date notice is served.</span></div>',
    '<div class="row"><div class="field"><label>Notice Served</label><div class="val">' + formatDate(n.noticeDate) + '</div></div>',
    '<div class="field"><label>Vacate By</label><div class="val">' + formatDate(n.vacateDate) + '</div></div>',
    '<div class="field"><label>Method of Service</label><div class="val">' + (n.serviceMethod||"Hand delivery") + '</div></div></div></div>',
    '<div class="legal"><strong>Important Information:</strong><br/>',
    '• Under Section 49 of the RTA, a landlord may end a tenancy for specific reasons with two months written notice.<br/>',
    '• The tenant is entitled to one month\'s rent as compensation if the landlord ends the tenancy for landlord\'s use.<br/>',
    '• The tenant may dispute this notice by applying for dispute resolution within 15 days of receiving it.<br/>',
    '• If the stated reason is not fulfilled, the landlord may be ordered to pay the tenant 12 months rent.<br/>',
    '• Residential Tenancy Branch: <strong>1-800-665-8779</strong> &nbsp;|&nbsp; www.gov.bc.ca/landlordtenant</div>',
    '<div class="sig-row"><div class="sig-block">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>',
    '<div class="sig-block">Tenant Acknowledgement &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div></div>',
    '<div class="watermark">Generated by PropDesk · propdesk.ca</div>',
    '</body></html>',
  ].join("\n");

  // Embed cursive signature
  const sigHtml = n.sigName
    ? '<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap" rel="stylesheet"/>'
      + '<div style="margin-top:24px"><div style="font-size:11px;color:#555;margin-bottom:4px">Landlord / Agent Signature</div>'
      + '<div class="cursive-sig" style="font-family:Dancing Script,Brush Script MT,cursive;font-size:28px;color:#000;border-bottom:1px solid #000;padding-bottom:4px;display:inline-block;min-width:200px">' + n.sigName + '</div>'
      + '<div style="font-size:11px;color:#555;margin-top:4px">' + new Date().toLocaleDateString("en-CA") + '</div></div>'
    : '<div style="margin-top:24px;border-top:1px solid #000;padding-top:6px;font-size:11px">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>';

  const finalHtml = html.replace('<div class="sig-row">', sigHtml + '\n<div class="sig-row" style="margin-top:20px">');

  const blob = new Blob([finalHtml], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "RTB-32_" + (n.tenantName||"tenant").replace(/\s+/g,"_") + "_" + (n.vacateDate||"notice") + ".html";
  a.click();
  URL.revokeObjectURL(url);
  return finalHtml;
};


// ── RTB-30 PDF Generator ───────────────────────────────────────────────────
const generateRTB30 = (n) => {
  const reasonLabels = n.allReasons || [n.reasonLabel || "See details below"];
  const reasonBoxes = reasonLabels.map((r, i) =>
    '<div class="reason-box"><span class="checkbox' + (i === (n.reasonIndex || 0) ? " checked" : "") + '"></span>' + r + '</div>'
  ).join("");

  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
    '<title>RTB-30 One Month Notice to End Tenancy</title>',
    '<style>',
    'body{font-family:Arial,sans-serif;font-size:13px;color:#000;margin:0;padding:40px}',
    '.header{text-align:center;border-bottom:2px solid #000;padding-bottom:16px;margin-bottom:24px}',
    '.header h1{font-size:18px;margin:0 0 4px}.header h2{font-size:14px;font-weight:normal;margin:0}',
    '.form-no{font-size:12px;color:#444;margin-top:6px}',
    '.section{margin-bottom:20px}.section-title{font-weight:bold;font-size:13px;border-bottom:1px solid #ccc;padding-bottom:4px;margin-bottom:12px}',
    '.row{display:flex;gap:24px;margin-bottom:10px}.field{flex:1}',
    '.field label{font-size:11px;color:#555;display:block;margin-bottom:2px}',
    '.field .val{border-bottom:1px solid #000;padding:4px 2px;min-height:22px;font-size:13px}',
    '.reason-box{border:1px solid #ccc;border-radius:4px;padding:10px 14px;margin:8px 0;background:#fafafa}',
    '.highlight{background:#f5f5f5;border:1px solid #ccc;border-radius:4px;padding:12px 16px;margin:16px 0}',
    '.legal{font-size:11px;color:#444;line-height:1.6;margin-top:20px;border-top:1px solid #ccc;padding-top:12px}',
    '.sig-row{display:flex;gap:40px;margin-top:32px}.sig-block{flex:1;border-top:1px solid #000;padding-top:6px;font-size:11px}',
    '.watermark{text-align:center;font-size:11px;color:#aaa;margin-top:40px}',
    '.checkbox{display:inline-block;width:14px;height:14px;border:1px solid #000;margin-right:8px;vertical-align:middle}',
    '.checked{background:#000}',
    '@media print{body{padding:20px}}',
    '</style></head><body>',
    '<div class="header"><h1>ONE MONTH NOTICE TO END TENANCY</h1>',
    '<h2>Residential Tenancy Act — British Columbia — Section 47</h2>',
    '<div class="form-no">Form RTB-30 &nbsp;|&nbsp; Generated by PropDesk &nbsp;|&nbsp; ' + new Date().toLocaleDateString("en-CA") + '</div></div>',
    '<div class="section"><div class="section-title">Rental Unit Information</div>',
    '<div class="row">',
    '<div class="field"><label>Tenant Name(s)</label><div class="val">' + (n.tenantName||"") + '</div></div>',
    '<div class="field"><label>Unit Number</label><div class="val">' + (n.unit||"") + '</div></div>',
    '</div>',
    '<div class="row">',
    '<div class="field"><label>Civic Address</label><div class="val">' + (n.address||"") + '</div></div>',
    '<div class="field"><label>City / Province</label><div class="val">' + (n.city||"Vancouver, BC") + '</div></div>',
    '</div></div>',
    '<div class="section"><div class="section-title">Landlord / Property Manager</div>',
    '<div class="row">',
    '<div class="field"><label>Name</label><div class="val">' + (n.landlordName||"") + '</div></div>',
    '<div class="field"><label>Phone</label><div class="val">' + (n.landlordPhone||"") + '</div></div>',
    '</div>',
    '<div class="row">',
    '<div class="field"><label>Mailing Address</label><div class="val">' + (n.landlordAddress||"") + '</div></div>',
    '<div class="field"><label>Email</label><div class="val">' + (n.landlordEmail||"") + '</div></div>',
    '</div></div>',
    '<div class="section"><div class="section-title">Reason for Notice</div>',
    reasonBoxes,
    (n.reasonIndex === 7 ? '<div class="row"><div class="field"><label>Describe reason</label><div class="val">' + (n.reasonDetail||"") + '</div></div></div>' : ''),
    '</div>',
    '<div class="section"><div class="section-title">Prior Written Warning</div>',
    '<div class="row">',
    '<div class="field"><label>Was a written warning given first?</label><div class="val">' + (n.warningGiven||"Yes") + '</div></div>',
    '<div class="field"><label>Date of Warning</label><div class="val">' + (n.warningDate ? formatDate(n.warningDate) : "") + '</div></div>',
    '</div></div>',
    '<div class="section"><div class="section-title">Notice Details</div>',
    '<div class="highlight">Tenant must vacate the rental unit by <strong>' + formatDate(n.vacateDate) + '</strong><br/>',
    '<span style="font-size:11px;color:#555">This date is at least one full calendar month from the date this notice is served.</span></div>',
    '<div class="row">',
    '<div class="field"><label>Date Notice Served</label><div class="val">' + formatDate(n.noticeDate) + '</div></div>',
    '<div class="field"><label>Vacate By Date</label><div class="val">' + formatDate(n.vacateDate) + '</div></div>',
    '<div class="field"><label>Method of Service</label><div class="val">' + (n.serviceMethod||"Hand delivery") + '</div></div>',
    '</div></div>',
    '<div class="legal"><strong>Important Information:</strong><br/>',
    '• Under Section 47 of the RTA, a landlord may end a tenancy for cause with one month written notice.<br/>',
    '• For most grounds, the landlord must first give the tenant a written opportunity to remedy the breach.<br/>',
    '• The tenant may dispute this notice by applying for dispute resolution within 15 days of receiving it.<br/>',
    '• If the tenant pays all rent owing within 5 days of a notice for unpaid rent, the notice is void.<br/>',
    '• Residential Tenancy Branch: <strong>1-800-665-8779</strong> &nbsp;|&nbsp; www.gov.bc.ca/landlordtenant</div>',
    '<div class="sig-row">',
    '<div class="sig-block">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>',
    '<div class="sig-block">Tenant Acknowledgement &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>',
    '</div>',
    '<div class="watermark">Generated by PropDesk · propdesk.ca</div>',
    '</body></html>',
  ].join("\n");

  // Embed cursive signature
  const sigHtml = n.sigName
    ? '<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap" rel="stylesheet"/>'
      + '<div style="margin-top:24px"><div style="font-size:11px;color:#555;margin-bottom:4px">Landlord / Agent Signature</div>'
      + '<div class="cursive-sig" style="font-family:Dancing Script,Brush Script MT,cursive;font-size:28px;color:#000;border-bottom:1px solid #000;padding-bottom:4px;display:inline-block;min-width:200px">' + n.sigName + '</div>'
      + '<div style="font-size:11px;color:#555;margin-top:4px">' + new Date().toLocaleDateString("en-CA") + '</div></div>'
    : '<div style="margin-top:24px;border-top:1px solid #000;padding-top:6px;font-size:11px">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>';

  const finalHtml = html.replace('<div class="sig-row">', sigHtml + '\n<div class="sig-row" style="margin-top:20px">');

  const blob = new Blob([finalHtml], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "RTB-30_" + (n.tenantName||"tenant").replace(/\s+/g,"_") + "_" + (n.vacateDate||"notice") + ".html";
  a.click();
  URL.revokeObjectURL(url);
  return finalHtml;
};

// ── BC Inspection Report Generator ────────────────────────────────────────
const generateInspection = (n) => {
  const rows = (n.rooms||[]).map((r) =>
    '<tr><td style="padding:8px;border:1px solid #ccc;font-weight:600">' + r.name + '</td>' +
    '<td style="padding:8px;border:1px solid #ccc">' + (r.moveInCondition||"") + '</td>' +
    '<td style="padding:8px;border:1px solid #ccc">' + (r.moveInNotes||"") + '</td>' +
    '<td style="padding:8px;border:1px solid #ccc">' + (r.moveOutCondition||"") + '</td>' +
    '<td style="padding:8px;border:1px solid #ccc">' + (r.moveOutNotes||"") + '</td></tr>'
  ).join("");

  const html = [
    '<!DOCTYPE html><html><head><meta charset="utf-8"/>',
    '<title>BC Condition Inspection Report</title>',
    '<style>',
    'body{font-family:Arial,sans-serif;font-size:13px;color:#000;margin:0;padding:40px}',
    '.header{text-align:center;border-bottom:2px solid #000;padding-bottom:16px;margin-bottom:24px}',
    '.header h1{font-size:18px;margin:0 0 4px}.header h2{font-size:14px;font-weight:normal;margin:0}',
    '.form-no{font-size:12px;color:#444;margin-top:6px}',
    '.section{margin-bottom:20px}.section-title{font-weight:bold;font-size:13px;border-bottom:1px solid #ccc;padding-bottom:4px;margin-bottom:12px}',
    '.row{display:flex;gap:24px;margin-bottom:10px}.field{flex:1}',
    '.field label{font-size:11px;color:#555;display:block;margin-bottom:2px}',
    '.field .val{border-bottom:1px solid #000;padding:4px 2px;min-height:22px;font-size:13px}',
    'table{width:100%;border-collapse:collapse;margin-top:8px}',
    'th{background:#f0f0f0;padding:8px;border:1px solid #ccc;font-size:12px;text-align:left}',
    '.legal{font-size:11px;color:#444;line-height:1.6;margin-top:20px;border-top:1px solid #ccc;padding-top:12px}',
    '.sig-row{display:flex;gap:40px;margin-top:16px}.sig-block{flex:1;border-top:1px solid #000;padding-top:6px;font-size:11px}',
    '.watermark{text-align:center;font-size:11px;color:#aaa;margin-top:40px}',
    '@media print{body{padding:20px}}',
    '</style></head><body>',
    '<div class="header"><h1>CONDITION INSPECTION REPORT</h1>',
    '<h2>Residential Tenancy Act — British Columbia — Section 23 &amp; 35</h2>',
    '<div class="form-no">Generated by PropDesk &nbsp;|&nbsp; ' + new Date().toLocaleDateString("en-CA") + '</div></div>',
    '<div class="section"><div class="section-title">Rental Unit Information</div>',
    '<div class="row"><div class="field"><label>Tenant Name(s)</label><div class="val">' + (n.tenantName||"") + '</div></div>',
    '<div class="field"><label>Unit Number</label><div class="val">' + (n.unit||"") + '</div></div></div>',
    '<div class="row"><div class="field"><label>Civic Address</label><div class="val">' + (n.address||"") + '</div></div>',
    '<div class="field"><label>City</label><div class="val">' + (n.city||"Vancouver, BC") + '</div></div></div>',
    '<div class="row"><div class="field"><label>Tenancy Start Date</label><div class="val">' + formatDate(n.moveInDate) + '</div></div>',
    '<div class="field"><label>Inspection Type</label><div class="val">' + (n.inspectionType||"Move-In") + '</div></div>',
    '<div class="field"><label>Inspection Date</label><div class="val">' + formatDate(n.inspectionDate) + '</div></div></div></div>',
    '<div class="section"><div class="section-title">Landlord / Property Manager</div>',
    '<div class="row"><div class="field"><label>Name</label><div class="val">' + (n.landlordName||"") + '</div></div>',
    '<div class="field"><label>Phone</label><div class="val">' + (n.landlordPhone||"") + '</div></div>',
    '<div class="field"><label>Email</label><div class="val">' + (n.landlordEmail||"") + '</div></div></div></div>',
    '<div class="section"><div class="section-title">Unit Condition by Room</div>',
    '<table><thead><tr>',
    '<th style="width:150px">Room / Area</th><th>Move-In Condition</th><th>Move-In Notes</th><th>Move-Out Condition</th><th>Move-Out Notes</th>',
    '</tr></thead><tbody>',
    rows || '<tr><td colspan="5" style="padding:12px;border:1px solid #ccc;text-align:center;color:#999">No rooms recorded</td></tr>',
    '</tbody></table></div>',
    '<div class="section"><div class="section-title">Keys &amp; Extras</div>',
    '<div class="row"><div class="field"><label>Keys Provided</label><div class="val">' + (n.keys||"") + '</div></div>',
    '<div class="field"><label>Parking Stall</label><div class="val">' + (n.parking||"") + '</div></div>',
    '<div class="field"><label>Storage Locker</label><div class="val">' + (n.storage||"") + '</div></div></div>',
    '<div class="row"><div class="field"><label>Additional Notes</label><div class="val" style="min-height:60px">' + (n.additionalNotes||"") + '</div></div></div></div>',
    '<div class="legal"><strong>Important Information:</strong><br/>',
    '• Landlords must complete a condition inspection at the start and end of every tenancy.<br/>',
    '• Both landlord and tenant must sign this report. The tenant is entitled to a copy.<br/>',
    '• A landlord who fails to complete a move-in inspection may not claim against the security deposit for damages.<br/>',
    '• Residential Tenancy Branch: <strong>1-800-665-8779</strong> &nbsp;|&nbsp; www.gov.bc.ca/landlordtenant</div>',
    '<div style="font-weight:bold;margin-top:32px;margin-bottom:8px">Move-In Signatures</div>',
    '<div class="sig-row"><div class="sig-block">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>',
    '<div class="sig-block">Tenant Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div></div>',
    '<div style="font-weight:bold;margin-top:28px;margin-bottom:8px">Move-Out Signatures</div>',
    '<div class="sig-row"><div class="sig-block">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>',
    '<div class="sig-block">Tenant Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div></div>',
    '<div class="watermark">Generated by PropDesk · propdesk.ca</div>',
    '</body></html>',
  ].join("\n");

  // Embed cursive signature
  const sigHtml = n.sigName
    ? '<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap" rel="stylesheet"/>'
      + '<div style="margin-top:24px"><div style="font-size:11px;color:#555;margin-bottom:4px">Landlord / Agent Signature</div>'
      + '<div class="cursive-sig" style="font-family:Dancing Script,Brush Script MT,cursive;font-size:28px;color:#000;border-bottom:1px solid #000;padding-bottom:4px;display:inline-block;min-width:200px">' + n.sigName + '</div>'
      + '<div style="font-size:11px;color:#555;margin-top:4px">' + new Date().toLocaleDateString("en-CA") + '</div></div>'
    : '<div style="margin-top:24px;border-top:1px solid #000;padding-top:6px;font-size:11px">Landlord / Agent Signature &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; Date</div>';

  const finalHtml = html.replace('<div class="sig-row">', sigHtml + '\n<div class="sig-row" style="margin-top:20px">');

  const blob = new Blob([finalHtml], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = "Inspection_" + (n.tenantName||"tenant").replace(/\s+/g,"_") + "_" + (n.inspectionDate||"report") + ".html";
  a.click();
  URL.revokeObjectURL(url);
  return finalHtml;
};

// ── Signature + Email Panel ────────────────────────────────────────────────
const SignaturePanel = ({ sigName, setSigName, onGenerate, onEmail, canGenerate, emailStatus, noticeType }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
    {/* Signature input */}
    <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>✍ Your Signature</div>
      <div style={{}}>
        <label style={{ fontSize: 11, color: "#9993A8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>Type your full name</label>
        <input
          style={{ width: "100%", background: "#0F0F14", border: "1px solid #ffffff12", borderRadius: 8, padding: "10px 12px", color: "#F0EDE8", fontSize: 13, outline: "none", boxSizing: "border-box" }}
          placeholder="e.g. Viren Bhattad"
          value={sigName}
          onChange={(e) => setSigName(e.target.value)}
        />
      </div>
      {sigName && (
        <div style={{ marginTop: 12, padding: "10px 16px", background: "#ffffff05", borderRadius: 8, borderBottom: "2px solid #F0EDE8" }}>
          <div style={{ fontSize: 11, color: "#5A5570", marginBottom: 4 }}>Preview:</div>
          <div style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontSize: 28, color: "#F0EDE8", lineHeight: 1.3 }}>
            {sigName}
          </div>
        </div>
      )}
    </div>

    {/* Generate button */}
    <button
      style={{
        padding: "14px", fontSize: 14, borderRadius: 12, border: "none", fontWeight: 600,
        background: canGenerate ? "#E8622A" : "#E8622A44",
        color: canGenerate ? "#fff" : "#E8622A88",
        cursor: canGenerate ? "pointer" : "not-allowed",
      }}
      onClick={onGenerate}
      disabled={!canGenerate}
    >
      📄 Generate {noticeType}
    </button>
    {!canGenerate && (
      <div style={{ fontSize: 12, color: "#5A5570", textAlign: "center", marginTop: -8 }}>Select property and tenant to generate</div>
    )}

    {/* Email button */}
    <button
      style={{
        padding: "13px", fontSize: 14, borderRadius: 12, border: "1px solid #2A7BE844", fontWeight: 600,
        background: emailStatus === "sent" ? "#22AA5518" : emailStatus === "sending" ? "#2A7BE818" : "#2A7BE810",
        color: emailStatus === "sent" ? "#22AA55" : emailStatus === "error" ? "#FF2D2D" : "#2A7BE8",
        cursor: canGenerate ? "pointer" : "not-allowed",
        opacity: canGenerate ? 1 : 0.4,
      }}
      onClick={onEmail}
      disabled={!canGenerate || emailStatus === "sending"}
    >
      {emailStatus === "sending" ? "⏳ Sending..." : emailStatus === "sent" ? "✅ Sent to Tenant" : emailStatus === "error" ? "❌ Failed — Retry" : "📧 Generate & Email to Tenant"}
    </button>
    {emailStatus === "sent" && (
      <div style={{ fontSize: 12, color: "#22AA55", textAlign: "center", marginTop: -8 }}>
        Notice emailed — delivery logged in Doc History
      </div>
    )}
  </div>
);

// ── Empty states ───────────────────────────────────────────────────────────
const EmptyState = ({ icon, title, subtitle }) => (
  <div style={{ padding: "48px 24px", textAlign: "center" }}>
    <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{title}</div>
    {subtitle && <div style={{ fontSize: 13, color: "#9993A8" }}>{subtitle}</div>}
  </div>
);


// -- Warning Letter Generator -----------------------------------------------
const generateWarning = (w) => {
  const html = [
    '<!DOCTYPE html><html><head><meta charset="UTF-8">',
    '<title>Warning Letter</title>',
    '<style>body{font-family:Arial,sans-serif;max-width:800px;margin:40px auto;padding:0 40px;color:#1a1a1a;font-size:13px;line-height:1.6}h1{font-size:18px;margin-bottom:4px}.header{border-bottom:3px solid #1a1a1a;padding-bottom:12px;margin-bottom:24px}.form-no{font-size:11px;color:#666;margin-top:4px}.section{margin-bottom:20px}.section-title{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#666;border-bottom:1px solid #ddd;padding-bottom:4px;margin-bottom:12px}.field{margin-bottom:8px;display:flex;gap:12px}.field label{font-weight:700;min-width:160px;font-size:12px}.val{flex:1;border-bottom:1px solid #ccc;min-height:18px;padding-bottom:2px}.body-text{background:#f9f9f9;border:1px solid #ddd;padding:16px;border-radius:4px;margin:16px 0;white-space:pre-wrap;line-height:1.8}.warn-badge{display:inline-block;background:#FF2D2D;color:white;padding:4px 12px;border-radius:4px;font-size:11px;font-weight:700;margin-bottom:16px}.sig-section{margin-top:32px}.sig-line{border-bottom:1px solid #333;margin-bottom:4px;height:36px;font-family:"Dancing Script",cursive;font-size:22px;line-height:36px;padding-left:4px}</style>',
    '<link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap" rel="stylesheet">',
    '</head><body>',
    '<div class="header"><h1>Warning Letter to Tenant</h1><div class="form-no">Generated by PropDesk | ' + new Date().toLocaleDateString('en-CA') + '</div></div>',
    '<div class="warn-badge">WARNING NOTICE</div>',
    '<div class="section"><div class="section-title">Tenant Information</div>',
    '<div class="field"><label>Tenant Name(s)</label><div class="val">' + (w.tenantName||'') + '</div></div>',
    '<div class="field"><label>Property Address</label><div class="val">' + (w.property||'') + '</div></div>',
    '<div class="field"><label>Unit</label><div class="val">' + (w.unit||'') + '</div></div>',
    '<div class="field"><label>Date of Notice</label><div class="val">' + (w.noticeDate||'') + '</div></div></div>',
    '<div class="section"><div class="section-title">Warning Details</div>',
    '<div class="field"><label>Warning Type</label><div class="val">' + (w.warningType||'') + '</div></div>',
    '<div class="field"><label>Incident Date</label><div class="val">' + (w.incidentDate||'') + '</div></div></div>',
    '<div class="section"><div class="section-title">Notice</div><div class="body-text">' + (w.bodyText||'') + '</div></div>',
    '<div class="section"><div class="section-title">Required Action</div><div class="body-text">' + (w.requiredAction||'No further action required at this time.') + '</div></div>',
    '<p style="font-size:12px;color:#666;margin-top:16px">This warning letter is issued pursuant to the Residential Tenancy Act of British Columbia. Failure to comply may result in a Notice to End Tenancy.</p>',
    '<div class="sig-section"><div class="section-title">Landlord / Property Manager</div>',
    '<div class="sig-line">' + (w.sigName||'') + '</div>',
    '<div style="font-size:11px;color:#666">' + (w.landlordName||'') + ' | ' + new Date().toLocaleDateString('en-CA') + '</div></div>',
    '</body></html>',
  ].join('');
  const blob = new Blob([html], { type: 'text/html' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = 'Warning_' + (w.tenantName||'tenant').replace(/\s+/g,'_') + '_' + (w.noticeDate||today()) + '.html';
  a.click();
  URL.revokeObjectURL(url);
  return html;
};

const WARNING_TYPES = [
  "Late Rent Payment", "Noise / Disturbance", "Unauthorized Occupant",
  "Unauthorized Pet", "Property Damage", "Lease Violation",
  "Smoking / Drug Use", "Illegal Activity", "Failure to Maintain Cleanliness",
  "Parking Violation", "Other",
];

const WARNING_BODY = {
  "Late Rent Payment":               "This letter serves as a formal warning that your rent payment was received late. As per your tenancy agreement and the Residential Tenancy Act of BC, rent is due on the 1st day of each month.\n\nContinued late payments may result in a Notice to End Tenancy.",
  "Noise / Disturbance":             "This letter serves as a formal warning regarding excessive noise and/or disturbances originating from your unit. Such disturbances violate your tenancy agreement and the right of other tenants to quiet enjoyment of their homes.",
  "Unauthorized Occupant":           "This letter serves as a formal warning that an unauthorized occupant has been observed residing in your unit. Your tenancy agreement requires all occupants be approved in writing by the landlord.",
  "Unauthorized Pet":                "This letter serves as a formal warning that an unauthorized pet has been observed in your unit. Your tenancy agreement does not permit pets without prior written approval from the landlord.",
  "Property Damage":                 "This letter serves as a formal warning regarding damage observed to the rental unit or common areas. Tenants are liable for damage beyond normal wear and tear.",
  "Lease Violation":                 "This letter serves as a formal warning that you are in violation of the terms of your tenancy agreement. Compliance with all terms is required under the Residential Tenancy Act of BC.",
  "Smoking / Drug Use":              "This letter serves as a formal warning regarding smoking or drug use in or around your unit. Your tenancy agreement prohibits smoking on the premises.",
  "Illegal Activity":                "This letter serves as a formal warning that illegal activity has been reported or observed in connection with your unit. The landlord has a legal obligation to maintain a safe environment for all tenants.",
  "Failure to Maintain Cleanliness": "This letter serves as a formal warning regarding the state of cleanliness of your unit. Tenants are required under the RTA to maintain their unit in a reasonably clean and sanitary condition.",
  "Parking Violation":               "This letter serves as a formal warning regarding a parking violation. The parking area is for vehicles registered to your tenancy agreement only.",
  "Other":                           "This letter serves as a formal warning regarding the matter described below.",
};

const WARNING_ACTION = {
  "Late Rent Payment":               "Please ensure all future rent payments are received by the 1st of each month. If you are experiencing financial difficulties, please contact us immediately.",
  "Noise / Disturbance":             "Please ensure noise levels are kept reasonable at all times, particularly between 10:00 PM and 8:00 AM. Failure to comply may result in a Notice to End Tenancy.",
  "Unauthorized Occupant":           "Please contact us within 7 days to have the unauthorized occupant vacate or submit an application for approval. Failure to comply may result in a Notice to End Tenancy.",
  "Unauthorized Pet":                "Please contact us within 7 days to remove the pet or submit a pet approval application. Failure to comply may result in a Notice to End Tenancy.",
  "Property Damage":                 "Please repair or arrange repair of the noted damage within 14 days. You may be held financially responsible. Failure to comply may result in a Notice to End Tenancy.",
  "Lease Violation":                 "Please remedy the noted violation within 7 days of receipt of this letter. Failure to comply may result in a Notice to End Tenancy.",
  "Smoking / Drug Use":              "Please cease all smoking and drug use on the premises immediately. Failure to comply will result in a Notice to End Tenancy.",
  "Illegal Activity":                "Any illegal activity must cease immediately. Failure to comply will result in a Notice to End Tenancy and may result in reporting to appropriate authorities.",
  "Failure to Maintain Cleanliness": "Please bring your unit to an acceptable standard of cleanliness within 7 days. The landlord reserves the right to arrange professional cleaning at the tenant expense.",
  "Parking Violation":               "Please ensure only registered vehicles are parked in designated spots. Unauthorized vehicles may be towed at the owner expense.",
  "Other":                           "Please take the necessary steps to remedy this matter within 7 days of receipt of this letter.",
};

// ── Main Component ─────────────────────────────────────────────────────────
const RentIncreaseTab = ({ properties = [], users = [], notices = [], onAddNotice, onUpdateNotice }) => {
  const [subTab,        setSubTab]        = useState("calculator");  // calculator | owners | history

  // Warning letter state
  const makeEmptyWarn = () => ({ tenantName:"", tenantEmail:"", property:"", propertyId:"", unit:"", noticeDate:today(), incidentDate:today(), warningType:"Late Rent Payment", bodyText:"", requiredAction:"", landlordName:"PropDesk Property Management", sigName:"" });
  const [warnForm,   setWarnForm]   = useState(makeEmptyWarn);
  const [warnPreview,setWarnPreview]= useState(false);
  const setW = (k,v) => setWarnForm((f) => ({...f, [k]:v}));
  const [selectedProp,  setSelectedProp]  = useState("");
  const [selectedUser,  setSelectedUser]  = useState("");
  const [currentRent,   setCurrentRent]   = useState("");
  const [customRate,    setCustomRate]     = useState(String(MAX_RATE));
  const [noticeDate,    setNoticeDate]     = useState(new Date().toISOString().split("T")[0]);
  const [effectiveDate, setEffectiveDate]  = useState(calcEffectiveDate());
  const [landlordName,  setLandlordName]   = useState("PropDesk Property Management");
  const [landlordEmail, setLandlordEmail]  = useState("");
  const [landlordPhone, setLandlordPhone]  = useState("");
  const [landlordAddr,  setLandlordAddr]   = useState("");
  // history comes from Supabase via notices prop
  const history = notices; // alias for template compatibility

  const addToHistory = async (entry) => {
    if (onAddNotice) {
      // Upload HTML to R2 first, save URL to Supabase
      let noticeUrl    = null;
      let storagePath  = null;
      if (entry.noticeHtml) {
        const r2 = await uploadNoticeToR2(entry.noticeHtml, entry.type, entry.tenantName);
        noticeUrl   = r2.fileUrl;
        storagePath = r2.storagePath;
      }
      await onAddNotice({
        ...entry,
        noticeUrl,
        storagePath,
        noticeHtml: null,  // don't store HTML in DB
        generatedOn: today(),
      });
    }
  };

  // ── Signature + Email state ──────────────────────────────────────────────
  const [sigName,       setSigName]        = useState("");
  const [emailStatus,   setEmailStatus]    = useState({});  // { [noticeId]: "sending"|"sent"|"error" }

  // ── Owners state ──────────────────────────────────────────────────────────
  const [owners,        setOwners]         = useState([]);
  const [showOwnerForm, setShowOwnerForm]  = useState(false);
  const [ownerForm,     setOwnerForm]      = useState({ name:"", email:"", phone:"", propertyId:"", mgmtFee:"", notes:"" });

  // Warning letter state
  const [warnProp,     setWarnProp]     = useState("");
  const [warnUser,     setWarnUser]     = useState("");
  const [warnType,     setWarnType]     = useState("Late Rent Payment");
  const [warnDate,     setWarnDate]     = useState(new Date().toISOString().split("T")[0]);
  const [warnIncident, setWarnIncident] = useState("");
  const [warnDetail,   setWarnDetail]   = useState("");
  const [warnFinal,    setWarnFinal]    = useState(false);

  // RTB-32 state
  const [rtb32Prop,     setRtb32Prop]      = useState("");
  const [rtb32User,     setRtb32User]      = useState("");
  const [rtb32Reason,   setRtb32Reason]    = useState(0);
  const [rtb32Detail,   setRtb32Detail]    = useState("");
  const [rtb32Notice,   setRtb32Notice]    = useState(new Date().toISOString().split("T")[0]);
  const [rtb32Vacate,   setRtb32Vacate]    = useState(() => { const d = new Date(); d.setMonth(d.getMonth() + 2); d.setDate(1); d.setMonth(d.getMonth() + 1); return d.toISOString().split("T")[0]; });
  const [rtb32Service,  setRtb32Service]   = useState("Hand delivery");

  // RTB-30 state
  const [rtb30Prop,     setRtb30Prop]      = useState("");
  const [rtb30User,     setRtb30User]      = useState("");
  const DEFAULT_RTB30_REASONS = [
    "Tenant has not paid rent",
    "Tenant is disturbing other occupants or the landlord",
    "Tenant is damaging or risking damage to the property",
    "Number of occupants exceeds what the tenancy allows",
    "Tenant or guest is engaging in illegal activity",
    "Tenant has misrepresented income for subsidized housing",
    "Tenant has repeatedly breached a material term of the tenancy",
  ];
  const [rtb30Reasons,  setRtb30Reasons]   = useState(DEFAULT_RTB30_REASONS);
  const [rtb30Reason,   setRtb30Reason]    = useState(0);
  const [rtb30Detail,   setRtb30Detail]    = useState("");
  const [rtb30NewReason,setRtb30NewReason] = useState("");
  const [rtb30EditIdx,  setRtb30EditIdx]   = useState(null);
  const [rtb30EditVal,  setRtb30EditVal]   = useState("");
  const [rtb30Notice,   setRtb30Notice]    = useState(new Date().toISOString().split("T")[0]);
  const [rtb30Vacate,   setRtb30Vacate]    = useState(() => { const d = new Date(); d.setMonth(d.getMonth() + 1); d.setDate(1); d.setMonth(d.getMonth() + 1); return d.toISOString().split("T")[0]; });
  const [rtb30Service,  setRtb30Service]   = useState("Hand delivery");
  const [rtb30Warning,  setRtb30Warning]   = useState("Yes");
  const [rtb30WarnDate, setRtb30WarnDate]  = useState("");

  // Inspection state
  const DEFAULT_ROOMS = [
    { name:"Living Room",   moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
    { name:"Kitchen",       moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
    { name:"Bathroom",      moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
    { name:"Bedroom 1",     moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
    { name:"Bedroom 2",     moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
    { name:"Hallway",       moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
    { name:"Balcony/Patio", moveInCondition:"", moveInNotes:"", moveOutCondition:"", moveOutNotes:"" },
  ];
  const [inspProp,      setInspProp]       = useState("");
  const [inspUser,      setInspUser]       = useState("");
  const [inspType,      setInspType]       = useState("Move-In");
  const [inspDate,      setInspDate]       = useState(new Date().toISOString().split("T")[0]);
  const [inspMoveIn,    setInspMoveIn]     = useState(new Date().toISOString().split("T")[0]);
  const [inspKeys,      setInspKeys]       = useState("");
  const [inspParking,   setInspParking]    = useState("");
  const [inspStorage,   setInspStorage]    = useState("");
  const [inspNotes,     setInspNotes]      = useState("");
  const [rooms,         setRooms]          = useState(DEFAULT_ROOMS);

  // ── Computed values ───────────────────────────────────────────────────────
  const rate          = Math.min(parseFloat(customRate) || 0, MAX_RATE);
  const current       = parseFloat(currentRent) || 0;
  const increaseAmt   = current * (rate / 100);
  const newRent       = current + increaseAmt;
  const overLimit     = parseFloat(customRate) > MAX_RATE;

  const renters       = users.filter((u) => u.role === "renter");
  const selectedRenter = renters.find((u) => String(u.id) === String(selectedUser));
  const selectedProperty = properties.find((p) => String(p.id) === String(selectedProp));

  const canGenerate   = selectedProp && selectedUser && current > 0 && effectiveDate;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleGenerate = () => {
    if (!canGenerate) return;
    const notice = {
      tenantName:      selectedRenter?.name || "Tenant",
      unit:            selectedRenter?.unit || "—",
      address:         selectedProperty?.address || selectedProperty?.name || "—",
      city:            "Vancouver, BC",
      landlordName,
      landlordEmail,
      landlordPhone,
      landlordAddress: landlordAddr,
      currentRent:     current,
      increaseAmount:  increaseAmt,
      newRent,
      increasePercent: rate,
      noticeDate,
      effectiveDate,
    };
    const noticeWithSig = { ...notice, sigName };
    const noticeHtml = generateRTB7(noticeWithSig);
    const noticeId = Date.now();
    addToHistory({
      type:         "RTB-7",
      tenantName:   notice.tenantName,
      tenantEmail:  selectedRenter?.email || "",
      property:     selectedProperty?.name || selectedProperty?.address || "—",
      unit:         notice.unit,
      currentRent:  current,
      newRent,
      rate,
      effectiveDate,
      noticeHtml,
      emailSent:    false,
    });
  };

  const handleAddOwner = () => {
    if (!ownerForm.name || !ownerForm.propertyId) return;
    setOwners((prev) => [...prev, { ...ownerForm, id: Date.now(), propertyId: parseInt(ownerForm.propertyId) }]);
    setOwnerForm({ name:"", email:"", phone:"", propertyId:"", mgmtFee:"", notes:"" });
    setShowOwnerForm(false);
  };

  // ── Sub-tab nav ────────────────────────────────────────────────────────────
  // ── RTB-32 handler ────────────────────────────────────────────────────────
  const handleGenerateRTB32 = () => {
    const renter   = renters.find((u) => String(u.id) === String(rtb32User));
    const property = properties.find((p) => String(p.id) === String(rtb32Prop));
    if (!renter || !property) return;
    const rtb32Data = {
      tenantName: renter.name, unit: renter.unit,
      address: property.address || property.name, city: "Vancouver, BC",
      landlordName, landlordEmail, landlordPhone, landlordAddress: landlordAddr,
      reasonIndex: rtb32Reason, reasonDetail: rtb32Detail, sigName,
      noticeDate: rtb32Notice, vacateDate: rtb32Vacate, serviceMethod: rtb32Service,
    };
    const rtb32Html = generateRTB32(rtb32Data);
    const rtb32Id = Date.now();
    addToHistory({
      type:         "RTB-32",
      tenantName:   renter.name,
      tenantEmail:  renter.email || "",
      property:     property.address || property.name,
      unit:         renter.unit,
      effectiveDate:rtb32Vacate,
      noticeHtml:   rtb32Html,
      emailSent:    false,
    });
  };

  // ── RTB-30 handler ────────────────────────────────────────────────────────
  const handleGenerateRTB30 = () => {
    const renter   = renters.find((u) => String(u.id) === String(rtb30User));
    const property = properties.find((p) => String(p.id) === String(rtb30Prop));
    if (!renter || !property) return;
    const rtb30Data = {
      tenantName: renter.name, unit: renter.unit,
      address: property.address || property.name, city: "Vancouver, BC",
      landlordName, landlordEmail, landlordPhone, landlordAddress: landlordAddr,
      reasonIndex: rtb30Reason, reasonDetail: rtb30Detail, reasonLabel: rtb30Reasons[rtb30Reason] || rtb30Detail, allReasons: rtb30Reasons,
      noticeDate: rtb30Notice, vacateDate: rtb30Vacate, serviceMethod: rtb30Service,
      warningGiven: rtb30Warning, warningDate: rtb30WarnDate, sigName,
    };
    const rtb30Html = generateRTB30(rtb30Data);
    const rtb30Id = Date.now();
    addToHistory({
      type:         "RTB-30",
      tenantName:   renter.name,
      tenantEmail:  renter.email || "",
      property:     property.address || property.name,
      unit:         renter.unit,
      effectiveDate:rtb30Vacate,
      noticeHtml:   rtb30Html,
      emailSent:    false,
    });
  };

  // ── Inspection handler ─────────────────────────────────────────────────────
  const handleGenerateInspection = () => {
    const renter   = renters.find((u) => String(u.id) === String(inspUser));
    const property = properties.find((p) => String(p.id) === String(inspProp));
    if (!renter || !property) return;
    const inspData = {
      tenantName: renter.name, unit: renter.unit,
      address: property.address || property.name, city: "Vancouver, BC",
      landlordName, landlordEmail, landlordPhone, sigName,
      inspectionType: inspType, inspectionDate: inspDate, moveInDate: inspMoveIn,
      keys: inspKeys, parking: inspParking, storage: inspStorage,
      additionalNotes: inspNotes, rooms,
    };
    const inspHtml = generateInspection(inspData);
    const inspId = Date.now();
    addToHistory({
      type:         "Inspection",
      tenantName:   renter.name,
      tenantEmail:  renter.email || "",
      property:     property.address || property.name,
      unit:         renter.unit,
      effectiveDate:inspDate,
      noticeHtml:   inspHtml,
      emailSent:    false,
    });
  };

  const updateRoom = (idx, field, val) => {
    setRooms((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: val } : r));
  };

  // ── Send notice email helper ──────────────────────────────────────────────
  const sendNotice = async ({ noticeId, noticeType, tenantEmail, tenantName, property, unit, effectiveDate, noticeHtml }) => {
    setEmailStatus((prev) => ({ ...prev, [noticeId]: "sending" }));
    const result = await sendNoticeEmail({
      toEmail: tenantEmail, toName: tenantName,
      noticeType, property, unit, effectiveDate,
      landlordName, noticeHtml,
    });
    setEmailStatus((prev) => ({ ...prev, [noticeId]: result.success ? "sent" : "error" }));
    if (result.success) {
      // Update emailSent flag in Supabase on the most recently added notice
      const latest = notices[0];
      if (latest?.id && onUpdateNotice) {
        onUpdateNotice(latest.id, { emailSent: true, emailSentAt: new Date().toISOString() });
      }
    }
    return result;
  };

  const tabs = [
    { id: "calculator", label: "🧮 Rent Calculator"  },
    { id: "rtb30",      label: "⚠️ RTB-30 Notice"    },
    { id: "rtb32",      label: "📋 RTB-32 Notice"    },
    { id: "inspection", label: "🔍 Inspection Report" },
    { id: "owners",     label: "🏠 Property Owners"  },
    { id: "warnings",   label: "⚠️ Warnings"          },
    { id: "history",    label: `📄 Doc History${history.length ? " (" + history.length + ")" : ""}` },
  ];

  return (
    <div>
      <div style={S.pageTitle}>Rent Increase & BC Forms</div>
      <div style={S.pageSub}>BC Residential Tenancy Act — Max allowable increase {CURRENT_YEAR}: <strong style={{ color: "#22AA55" }}>{MAX_RATE}%</strong></div>

      {/* Sub-tabs */}
      <div style={{ ...S.tabRow, maxWidth: 480, marginBottom: 28 }}>
        {tabs.map(({ id, label }) => (
          <button key={id} style={{ ...S.tabBtn, ...(subTab === id ? S.tabBtnActive : {}) }} onClick={() => setSubTab(id)}>
            {label}
          </button>
        ))}
      </div>

      {/* ── CALCULATOR TAB ── */}
      {subTab === "calculator" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, maxWidth: 900 }}>

          {/* Left — Inputs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* BC Rate Banner */}
            <div style={{ background: "#22AA5510", border: "1px solid #22AA5530", borderRadius: 12, padding: "14px 18px" }}>
              <div style={{ fontSize: 12, color: "#22AA55", fontWeight: 700, marginBottom: 2 }}>BC MAXIMUM {CURRENT_YEAR}</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#22AA55" }}>{MAX_RATE}%</div>
              <div style={{ fontSize: 11, color: "#9993A8", marginTop: 2 }}>Set by the Province of British Columbia</div>
            </div>

            {/* Property */}
            <div style={S.field}>
              <label style={S.label}>Property</label>
              <select style={S.select} value={selectedProp} onChange={(e) => { setSelectedProp(e.target.value); setSelectedUser(""); }}>
                <option value="">— Select property —</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
              </select>
            </div>

            {/* Renter */}
            <div style={S.field}>
              <label style={S.label}>Tenant</label>
              <select style={S.select} value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} disabled={!selectedProp}>
                <option value="">— Select tenant —</option>
                {renters.filter((u) => !selectedProp || String(u.propertyId) === String(selectedProp)).map((u) => (
                  <option key={u.id} value={u.id}>{u.name} — Unit {u.unit}</option>
                ))}
              </select>
            </div>

            {/* Current Rent */}
            <div style={S.field}>
              <label style={S.label}>Current Monthly Rent ($)</label>
              <input style={S.input} type="number" placeholder="e.g. 2200" value={currentRent} onChange={(e) => setCurrentRent(e.target.value)} />
            </div>

            {/* Increase % */}
            <div style={S.field}>
              <label style={S.label}>Increase % (max {MAX_RATE}%)</label>
              <input
                style={{ ...S.input, borderColor: overLimit ? "#FF2D2D" : undefined }}
                type="number" step="0.1" min="0" max={MAX_RATE}
                value={customRate}
                onChange={(e) => setCustomRate(e.target.value)}
              />
              {overLimit && (
                <div style={{ fontSize: 11, color: "#FF2D2D", marginTop: 4 }}>
                  ⚠ Exceeds BC maximum of {MAX_RATE}% — capped automatically
                </div>
              )}
            </div>

            {/* Dates */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={S.field}>
                <label style={S.label}>Notice Served Date</label>
                <input style={S.input} type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Effective Date</label>
                <input style={S.input} type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
              </div>
            </div>

            {/* Landlord info */}
            <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Info (appears on notice)</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input style={S.input} placeholder="Landlord / Agent Name" value={landlordName}  onChange={(e) => setLandlordName(e.target.value)} />
                <input style={S.input} placeholder="Email"                  value={landlordEmail} onChange={(e) => setLandlordEmail(e.target.value)} />
                <input style={S.input} placeholder="Phone"                  value={landlordPhone} onChange={(e) => setLandlordPhone(e.target.value)} />
                <input style={S.input} placeholder="Mailing address"        value={landlordAddr}  onChange={(e) => setLandlordAddr(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Right — Result + Generate */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Result card */}
            <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 16, padding: "28px 24px" }}>
              <div style={{ fontSize: 13, color: "#9993A8", fontWeight: 600, marginBottom: 20 }}>RENT INCREASE SUMMARY</div>

              {[
                { label: "Current Rent",      value: current > 0 ? fmtC(current)     : "—",         color: "#F0EDE8" },
                { label: `Increase (${rate}%)`, value: current > 0 ? `+ ${fmtC(increaseAmt)}` : "—", color: "#E8622A" },
                { label: "New Monthly Rent",  value: current > 0 ? fmtC(newRent)     : "—",         color: "#22AA55", big: true },
              ].map(({ label, value, color, big }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 0", borderBottom: "1px solid #ffffff08" }}>
                  <span style={{ fontSize: 13, color: "#9993A8" }}>{label}</span>
                  <span style={{ fontSize: big ? 26 : 16, fontWeight: big ? 800 : 600, color }}>{value}</span>
                </div>
              ))}

              <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9993A8" }}>
                  <span>Notice Date</span><span style={{ color: "#F0EDE8" }}>{formatDate(noticeDate)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9993A8" }}>
                  <span>Effective Date</span><span style={{ color: "#F0EDE8" }}>{formatDate(effectiveDate)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#9993A8" }}>
                  <span>Annual Increase</span>
                  <span style={{ color: "#F0EDE8" }}>{current > 0 ? fmtC(increaseAmt * 12) + "/yr" : "—"}</span>
                </div>
              </div>
            </div>

            {/* Tenant info preview */}
            {selectedRenter && (
              <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tenant</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{selectedRenter.name}</div>
                <div style={{ fontSize: 12, color: "#9993A8", marginTop: 2 }}>{selectedRenter.email}</div>
                <div style={{ fontSize: 12, color: "#9993A8" }}>Unit {selectedRenter.unit} · {selectedProperty?.address || selectedProperty?.name}</div>
              </div>
            )}

            {/* 3-month notice reminder */}
            <div style={{ background: "#2A7BE810", border: "1px solid #2A7BE830", borderRadius: 12, padding: "14px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#2A7BE8", marginBottom: 6 }}>📅 3-MONTH NOTICE REQUIRED</div>
              <div style={{ fontSize: 12, color: "#9993A8", lineHeight: 1.6 }}>
                Under the BC Residential Tenancy Act, you must give tenants at least <strong style={{ color: "#F0EDE8" }}>3 full calendar months</strong> notice before the effective date. The increase can only happen on the anniversary of the tenancy or rent increase.
              </div>
            </div>

            {/* Signature + Generate + Email */}
            <SignaturePanel
              sigName={sigName} setSigName={setSigName}
              canGenerate={canGenerate}
              noticeType="RTB-7 Notice"
              emailStatus={emailStatus["rtb7_latest"]}
              onGenerate={handleGenerate}
              onEmail={async () => {
                const latest = history.find((h) => !h.type || h.type === "RTB-7");
                if (!latest?.noticeHtml) { handleGenerate(); return; }
                await sendNotice({
                  noticeId: "rtb7_latest", noticeType: "RTB-7 Notice of Rent Increase",
                  tenantEmail: latest.tenantEmail, tenantName: latest.tenantName,
                  property: latest.property, unit: latest.unit,
                  effectiveDate: latest.effectiveDate, noticeHtml: latest.noticeHtml,
                });
              }}
            />

            {/* BC Rate history */}
            <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>BC Allowable Rates</div>
              {Object.entries(BC_RATES).reverse().map(([year, rate]) => (
                <div key={year} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #ffffff06" }}>
                  <span style={{ fontSize: 13, color: parseInt(year) === CURRENT_YEAR ? "#F0EDE8" : "#9993A8", fontWeight: parseInt(year) === CURRENT_YEAR ? 700 : 400 }}>
                    {year} {parseInt(year) === CURRENT_YEAR ? "← current" : ""}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: parseInt(year) === CURRENT_YEAR ? "#22AA55" : "#9993A8" }}>{rate}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── RTB-30 TAB ── */}
      {subTab === "rtb30" && (
        <div style={{ maxWidth: 900 }}>
          <div style={{ background: "#FF2D2D10", border: "1px solid #FF2D2D30", borderRadius: 12, padding: "14px 18px", marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#FF2D2D", marginBottom: 4 }}>⚠ ONE MONTH NOTICE TO END TENANCY — Section 47 RTA</div>
            <div style={{ fontSize: 12, color: "#9993A8" }}>Use this notice to end a tenancy for tenant breach — unpaid rent, disturbance, damage, illegal activity, or other cause. Tenant has 15 days to dispute. For unpaid rent, notice is void if tenant pays within 5 days.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

            {/* Left — inputs */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={S.field}>
                <label style={S.label}>Property</label>
                <select style={S.select} value={rtb30Prop} onChange={(e) => { setRtb30Prop(e.target.value); setRtb30User(""); }}>
                  <option value="">— Select property —</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
                </select>
              </div>
              <div style={S.field}>
                <label style={S.label}>Tenant</label>
                <select style={S.select} value={rtb30User} onChange={(e) => setRtb30User(e.target.value)} disabled={!rtb30Prop}>
                  <option value="">— Select tenant —</option>
                  {renters.filter((u) => !rtb30Prop || String(u.propertyId) === String(rtb30Prop)).map((u) => (
                    <option key={u.id} value={u.id}>{u.name} — Unit {u.unit}</option>
                  ))}
                </select>
              </div>
              <div style={S.field}>
                <label style={S.label}>Reason for Notice</label>

                {/* Dynamic reasons list */}
                {rtb30Reasons.map((reason, i) => (
                  <div key={i} style={{ marginBottom: 7 }}>
                    {rtb30EditIdx === i ? (
                      <div style={{ display: "flex", gap: 6 }}>
                        <input
                          style={{ ...S.input, flex: 1, fontSize: 12 }}
                          value={rtb30EditVal}
                          onChange={(e) => setRtb30EditVal(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { setRtb30Reasons((prev) => prev.map((r, j) => j === i ? rtb30EditVal : r)); setRtb30EditIdx(null); }
                            if (e.key === "Escape") setRtb30EditIdx(null);
                          }}
                          autoFocus
                        />
                        <button style={{ ...S.btnSmall, background: "#22AA55", color: "#fff", fontSize: 11 }}
                          onClick={() => { setRtb30Reasons((prev) => prev.map((r, j) => j === i ? rtb30EditVal : r)); setRtb30EditIdx(null); }}>✓</button>
                        <button style={{ ...S.btnSmall, background: "#ffffff10", color: "#9993A8", fontSize: 11 }}
                          onClick={() => setRtb30EditIdx(null)}>✕</button>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div
                          onClick={() => setRtb30Reason(i)}
                          style={{
                            flex: 1, padding: "9px 14px", borderRadius: 8,
                            border: rtb30Reason === i ? "2px solid #FF2D2D" : "1px solid #ffffff15",
                            background: rtb30Reason === i ? "#FF2D2D10" : "#0F0F14",
                            cursor: "pointer", fontSize: 13,
                            color: rtb30Reason === i ? "#F0EDE8" : "#9993A8",
                          }}
                        >
                          <span style={{ marginRight: 8 }}>{rtb30Reason === i ? "●" : "○"}</span>{reason}
                        </div>
                        <button
                          title="Edit reason"
                          style={{ ...S.btnSmall, background: "#ffffff08", color: "#9993A8", fontSize: 11, padding: "6px 8px" }}
                          onClick={() => { setRtb30EditIdx(i); setRtb30EditVal(reason); }}
                        >✎</button>
                        <button
                          title="Move up"
                          style={{ ...S.btnSmall, background: "#ffffff08", color: "#9993A8", fontSize: 11, padding: "6px 8px" }}
                          onClick={() => {
                            if (i === 0) return;
                            setRtb30Reasons((prev) => { const a = [...prev]; [a[i-1], a[i]] = [a[i], a[i-1]]; return a; });
                            if (rtb30Reason === i) setRtb30Reason(i - 1);
                            else if (rtb30Reason === i - 1) setRtb30Reason(i);
                          }}
                        >↑</button>
                        <button
                          title="Delete reason"
                          style={{ ...S.btnSmall, background: "#FF2D2D15", color: "#FF2D2D", fontSize: 11, padding: "6px 8px" }}
                          onClick={() => {
                            setRtb30Reasons((prev) => prev.filter((_, j) => j !== i));
                            if (rtb30Reason >= i) setRtb30Reason(Math.max(0, rtb30Reason - 1));
                          }}
                        >✕</button>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add custom reason */}
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <input
                    style={{ ...S.input, flex: 1, fontSize: 12 }}
                    placeholder="+ Add custom reason..."
                    value={rtb30NewReason}
                    onChange={(e) => setRtb30NewReason(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && rtb30NewReason.trim()) {
                        setRtb30Reasons((prev) => [...prev, rtb30NewReason.trim()]);
                        setRtb30Reason(rtb30Reasons.length);
                        setRtb30NewReason("");
                      }
                    }}
                  />
                  <button
                    style={{ ...S.btnSmall, background: rtb30NewReason.trim() ? "#E8622A" : "#ffffff10", color: rtb30NewReason.trim() ? "#fff" : "#5A5570", fontSize: 12 }}
                    disabled={!rtb30NewReason.trim()}
                    onClick={() => {
                      if (!rtb30NewReason.trim()) return;
                      setRtb30Reasons((prev) => [...prev, rtb30NewReason.trim()]);
                      setRtb30Reason(rtb30Reasons.length);
                      setRtb30NewReason("");
                    }}
                  >Add</button>
                </div>
                <div style={{ fontSize: 11, color: "#5A5570", marginTop: 4 }}>Click ✎ to edit · ↑ to reorder · ✕ to remove · press Enter to add</div>

                {/* Detail box for any selected reason if needed */}
                <div style={{ marginTop: 10 }}>
                  <label style={S.label}>Additional Detail (optional)</label>
                  <textarea style={{ ...S.textarea, minHeight: 60 }} placeholder="Add specific details about this reason..." value={rtb30Detail} onChange={(e) => setRtb30Detail(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Right — dates + warning + generate */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={S.field}>
                  <label style={S.label}>Notice Served Date</label>
                  <input style={S.input} type="date" value={rtb30Notice} onChange={(e) => setRtb30Notice(e.target.value)} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Vacate By Date</label>
                  <input style={S.input} type="date" value={rtb30Vacate} onChange={(e) => setRtb30Vacate(e.target.value)} />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Method of Service</label>
                <select style={S.select} value={rtb30Service} onChange={(e) => setRtb30Service(e.target.value)}>
                  {["Hand delivery", "Mail", "Email (if agreed)", "Posted on door"].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              {/* Prior warning */}
              <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Prior Written Warning</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div style={S.field}>
                    <label style={S.label}>Warning Given?</label>
                    <select style={S.select} value={rtb30Warning} onChange={(e) => setRtb30Warning(e.target.value)}>
                      {["Yes", "No", "N/A (unpaid rent)"].map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div style={S.field}>
                    <label style={S.label}>Date of Warning</label>
                    <input style={S.input} type="date" value={rtb30WarnDate} onChange={(e) => setRtb30WarnDate(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Landlord info */}
              <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Info (appears on notice)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input style={S.input} placeholder="Landlord / Agent Name" value={landlordName}  onChange={(e) => setLandlordName(e.target.value)} />
                  <input style={S.input} placeholder="Email"                  value={landlordEmail} onChange={(e) => setLandlordEmail(e.target.value)} />
                  <input style={S.input} placeholder="Phone"                  value={landlordPhone} onChange={(e) => setLandlordPhone(e.target.value)} />
                  <input style={S.input} placeholder="Mailing address"        value={landlordAddr}  onChange={(e) => setLandlordAddr(e.target.value)} />
                </div>
              </div>

              {/* Unpaid rent reminder */}
              {rtb30Reasons[rtb30Reason]?.toLowerCase().includes("rent") && (
                <div style={{ background: "#22AA5510", border: "1px solid #22AA5530", borderRadius: 10, padding: "12px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#22AA55", marginBottom: 4 }}>💡 Unpaid Rent Rule</div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>If the reason is unpaid rent, the notice becomes <strong style={{ color: "#F0EDE8" }}>void</strong> if the tenant pays all rent owing within <strong style={{ color: "#F0EDE8" }}>5 days</strong> of receiving this notice.</div>
                </div>
              )}

              <SignaturePanel
                sigName={sigName} setSigName={setSigName}
                canGenerate={!!(rtb30Prop && rtb30User)}
                noticeType="RTB-30 Notice"
                emailStatus={emailStatus["rtb30_latest"]}
                onGenerate={handleGenerateRTB30}
                onEmail={async () => {
                  const latest = history.find((h) => h.type === "RTB-30");
                  if (!latest?.noticeHtml) { handleGenerateRTB30(); return; }
                  await sendNotice({
                    noticeId: "rtb30_latest", noticeType: "RTB-30 One Month Notice to End Tenancy",
                    tenantEmail: latest.tenantEmail, tenantName: latest.tenantName,
                    property: latest.property, unit: latest.unit,
                    effectiveDate: latest.effectiveDate, noticeHtml: latest.noticeHtml,
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── RTB-32 TAB ── */}
      {subTab === "rtb32" && (
        <div style={{ maxWidth: 900 }}>
          <div style={{ background: "#FF7A2210", border: "1px solid #FF7A2230", borderRadius: 12, padding: "14px 18px", marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#FF7A22", marginBottom: 4 }}>⚠ TWO MONTH NOTICE TO END TENANCY — Section 49 RTA</div>
            <div style={{ fontSize: 12, color: "#9993A8" }}>Use this notice to end a tenancy for landlord use, sale, or conversion. Tenant must receive at least 2 full calendar months notice. Tenant may be entitled to 1 month rent compensation.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={S.field}>
                <label style={S.label}>Property</label>
                <select style={S.select} value={rtb32Prop} onChange={(e) => { setRtb32Prop(e.target.value); setRtb32User(""); }}>
                  <option value="">— Select property —</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
                </select>
              </div>
              <div style={S.field}>
                <label style={S.label}>Tenant</label>
                <select style={S.select} value={rtb32User} onChange={(e) => setRtb32User(e.target.value)} disabled={!rtb32Prop}>
                  <option value="">— Select tenant —</option>
                  {renters.filter((u) => !rtb32Prop || String(u.propertyId) === String(rtb32Prop)).map((u) => (
                    <option key={u.id} value={u.id}>{u.name} — Unit {u.unit}</option>
                  ))}
                </select>
              </div>
              <div style={S.field}>
                <label style={S.label}>Reason for Ending Tenancy</label>
                {[
                  "Landlord's use of property (landlord or close family member will occupy)",
                  "Sale of property (buyer or buyer's family member will occupy)",
                  "Conversion to non-rental use (strata, demolition, rezoning)",
                  "Other reason under Section 49",
                ].map((reason, i) => (
                  <div
                    key={i}
                    onClick={() => setRtb32Reason(i)}
                    style={{
                      padding: "10px 14px", marginBottom: 8, borderRadius: 8,
                      border: rtb32Reason === i ? "2px solid #E8622A" : "1px solid #ffffff15",
                      background: rtb32Reason === i ? "#E8622A10" : "#0F0F14",
                      cursor: "pointer", fontSize: 13,
                      color: rtb32Reason === i ? "#F0EDE8" : "#9993A8",
                    }}
                  >
                    <span style={{ marginRight: 8 }}>{rtb32Reason === i ? "●" : "○"}</span>{reason}
                  </div>
                ))}
                {rtb32Reason === 3 && (
                  <textarea style={S.textarea} placeholder="Describe the reason..." value={rtb32Detail} onChange={(e) => setRtb32Detail(e.target.value)} />
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={S.field}>
                  <label style={S.label}>Notice Served Date</label>
                  <input style={S.input} type="date" value={rtb32Notice} onChange={(e) => setRtb32Notice(e.target.value)} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Vacate By Date</label>
                  <input style={S.input} type="date" value={rtb32Vacate} onChange={(e) => setRtb32Vacate(e.target.value)} />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Method of Service</label>
                <select style={S.select} value={rtb32Service} onChange={(e) => setRtb32Service(e.target.value)}>
                  {["Hand delivery", "Mail", "Email (if agreed)", "Posted on door"].map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Info (appears on notice)</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input style={S.input} placeholder="Landlord / Agent Name" value={landlordName}  onChange={(e) => setLandlordName(e.target.value)} />
                  <input style={S.input} placeholder="Email"                  value={landlordEmail} onChange={(e) => setLandlordEmail(e.target.value)} />
                  <input style={S.input} placeholder="Phone"                  value={landlordPhone} onChange={(e) => setLandlordPhone(e.target.value)} />
                  <input style={S.input} placeholder="Mailing address"        value={landlordAddr}  onChange={(e) => setLandlordAddr(e.target.value)} />
                </div>
              </div>
              <div style={{ background: "#FF7A2210", border: "1px solid #FF7A2230", borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#FF7A22", marginBottom: 4 }}>💰 Compensation Reminder</div>
                <div style={{ fontSize: 12, color: "#9993A8" }}>If ending for landlord use or sale, tenant is entitled to <strong style={{ color: "#F0EDE8" }}>1 month rent compensation</strong>. Deduct from last month or pay separately.</div>
              </div>
              <SignaturePanel
                sigName={sigName} setSigName={setSigName}
                canGenerate={!!(rtb32Prop && rtb32User)}
                noticeType="RTB-32 Notice"
                emailStatus={emailStatus["rtb32_latest"]}
                onGenerate={handleGenerateRTB32}
                onEmail={async () => {
                  const latest = history.find((h) => h.type === "RTB-32");
                  if (!latest || !latest.noticeHtml) { handleGenerateRTB32(); return; }
                  await sendNotice({
                    noticeId: "rtb32_latest",
                    noticeType: "RTB-32 Two Month Notice to End Tenancy",
                    tenantEmail: latest.tenantEmail,
                    tenantName: latest.tenantName,
                    property: latest.property,
                    unit: latest.unit,
                    effectiveDate: latest.effectiveDate,
                    noticeHtml: latest.noticeHtml,
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── INSPECTION TAB ── */}
      {subTab === "inspection" && (
        <div style={{ maxWidth: 960 }}>
          <div style={{ background: "#2A7BE810", border: "1px solid #2A7BE830", borderRadius: 12, padding: "14px 18px", marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#2A7BE8", marginBottom: 4 }}>🔍 BC CONDITION INSPECTION REPORT — Section 23 &amp; 35 RTA</div>
            <div style={{ fontSize: 12, color: "#9993A8" }}>Required by BC law at move-in and move-out. Both parties must sign. Failure to complete move-in inspection may prevent security deposit claims.</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 24 }}>

            {/* Left panel — details */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={S.field}>
                <label style={S.label}>Property</label>
                <select style={S.select} value={inspProp} onChange={(e) => { setInspProp(e.target.value); setInspUser(""); }}>
                  <option value="">— Select property —</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
                </select>
              </div>
              <div style={S.field}>
                <label style={S.label}>Tenant</label>
                <select style={S.select} value={inspUser} onChange={(e) => setInspUser(e.target.value)} disabled={!inspProp}>
                  <option value="">— Select tenant —</option>
                  {renters.filter((u) => !inspProp || String(u.propertyId) === String(inspProp)).map((u) => (
                    <option key={u.id} value={u.id}>{u.name} — Unit {u.unit}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={S.field}>
                  <label style={S.label}>Inspection Type</label>
                  <select style={S.select} value={inspType} onChange={(e) => setInspType(e.target.value)}>
                    {["Move-In", "Move-Out", "Annual", "Pre-Sale"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={S.field}>
                  <label style={S.label}>Inspection Date</label>
                  <input style={S.input} type="date" value={inspDate} onChange={(e) => setInspDate(e.target.value)} />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Tenancy Start Date</label>
                <input style={S.input} type="date" value={inspMoveIn} onChange={(e) => setInspMoveIn(e.target.value)} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <div style={S.field}><label style={S.label}>Keys</label><input style={S.input} placeholder="e.g. 2" value={inspKeys} onChange={(e) => setInspKeys(e.target.value)} /></div>
                <div style={S.field}><label style={S.label}>Parking</label><input style={S.input} placeholder="Stall #" value={inspParking} onChange={(e) => setInspParking(e.target.value)} /></div>
                <div style={S.field}><label style={S.label}>Storage</label><input style={S.input} placeholder="Locker #" value={inspStorage} onChange={(e) => setInspStorage(e.target.value)} /></div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Additional Notes</label>
                <textarea style={S.textarea} placeholder="Overall notes, special conditions..." value={inspNotes} onChange={(e) => setInspNotes(e.target.value)} />
              </div>
              <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, padding: "16px 18px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#9993A8", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>Your Info</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <input style={S.input} placeholder="Landlord / Agent Name" value={landlordName}  onChange={(e) => setLandlordName(e.target.value)} />
                  <input style={S.input} placeholder="Phone"                  value={landlordPhone} onChange={(e) => setLandlordPhone(e.target.value)} />
                  <input style={S.input} placeholder="Email"                  value={landlordEmail} onChange={(e) => setLandlordEmail(e.target.value)} />
                </div>
              </div>
              <SignaturePanel
                sigName={sigName} setSigName={setSigName}
                canGenerate={!!(inspProp && inspUser)}
                noticeType="Inspection Report"
                emailStatus={emailStatus["insp_latest"]}
                onGenerate={handleGenerateInspection}
                onEmail={async () => {
                  const latest = history.find((h) => h.type === "Inspection");
                  if (!latest?.noticeHtml) { handleGenerateInspection(); return; }
                  await sendNotice({
                    noticeId: "insp_latest", noticeType: "Condition Inspection Report",
                    tenantEmail: latest.tenantEmail, tenantName: latest.tenantName,
                    property: latest.property, unit: latest.unit,
                    effectiveDate: latest.effectiveDate, noticeHtml: latest.noticeHtml,
                  });
                }}
              />
            </div>

            {/* Right panel — room condition grid */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Room Conditions</div>
              <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 1fr 1fr 1fr", gap: 8, padding: "10px 14px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
                  {["Room", "Move-In Cond.", "Move-In Notes", "Move-Out Cond.", "Move-Out Notes"].map((h) => (
                    <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
                  ))}
                </div>
                {rooms.map((room, idx) => (
                  <div key={idx} style={{ display: "grid", gridTemplateColumns: "130px 1fr 1fr 1fr 1fr", gap: 8, padding: "10px 14px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#F0EDE8" }}>{room.name}</div>
                    <select
                      style={{ ...S.select, padding: "6px 8px", fontSize: 12 }}
                      value={room.moveInCondition}
                      onChange={(e) => updateRoom(idx, "moveInCondition", e.target.value)}
                    >
                      {["", "Good", "Fair", "Poor", "N/A"].map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                    </select>
                    <input style={{ ...S.input, padding: "6px 8px", fontSize: 12 }} placeholder="Notes..." value={room.moveInNotes} onChange={(e) => updateRoom(idx, "moveInNotes", e.target.value)} />
                    <select
                      style={{ ...S.select, padding: "6px 8px", fontSize: 12 }}
                      value={room.moveOutCondition}
                      onChange={(e) => updateRoom(idx, "moveOutCondition", e.target.value)}
                    >
                      {["", "Good", "Fair", "Poor", "N/A"].map((o) => <option key={o} value={o}>{o || "—"}</option>)}
                    </select>
                    <input style={{ ...S.input, padding: "6px 8px", fontSize: 12 }} placeholder="Notes..." value={room.moveOutNotes} onChange={(e) => updateRoom(idx, "moveOutNotes", e.target.value)} />
                  </div>
                ))}
              </div>
              <button
                style={{ ...S.btnSecondary, marginTop: 10, fontSize: 12, padding: "7px 14px", borderRadius: 8 }}
                onClick={() => setRooms((prev) => [...prev, { name: "New Room", moveInCondition: "", moveInNotes: "", moveOutCondition: "", moveOutNotes: "" }])}
              >
                + Add Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── OWNERS TAB ── */}
      {subTab === "owners" && (
        <div style={{ maxWidth: 860 }}>
          <div style={S.tableWrap}>
            <div style={S.tableHeader}>
              <div style={S.tableTitle}>Property Owners</div>
              <button style={{ ...S.btnPrimary, fontSize: 12, padding: "8px 16px" }} onClick={() => setShowOwnerForm(true)}>
                + Add Owner
              </button>
            </div>

            {/* Add Owner Form */}
            {showOwnerForm && (
              <div style={{ padding: "20px 24px", borderBottom: "1px solid #ffffff08", background: "#0F0F14" }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>New Property Owner</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={S.label}>Full Name *</label>
                    <input style={S.input} placeholder="Owner name" value={ownerForm.name} onChange={(e) => setOwnerForm({ ...ownerForm, name: e.target.value })} />
                  </div>
                  <div>
                    <label style={S.label}>Email</label>
                    <input style={S.input} placeholder="owner@email.com" value={ownerForm.email} onChange={(e) => setOwnerForm({ ...ownerForm, email: e.target.value })} />
                  </div>
                  <div>
                    <label style={S.label}>Phone</label>
                    <input style={S.input} placeholder="604-555-0000" value={ownerForm.phone} onChange={(e) => setOwnerForm({ ...ownerForm, phone: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
                  <div>
                    <label style={S.label}>Property *</label>
                    <select style={S.select} value={ownerForm.propertyId} onChange={(e) => setOwnerForm({ ...ownerForm, propertyId: e.target.value })}>
                      <option value="">— Select —</option>
                      {properties.map((p) => <option key={p.id} value={p.id}>{p.address || p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={S.label}>Monthly Mgmt Fee ($)</label>
                    <input style={S.input} type="number" placeholder="e.g. 350" value={ownerForm.mgmtFee} onChange={(e) => setOwnerForm({ ...ownerForm, mgmtFee: e.target.value })} />
                  </div>
                  <div>
                    <label style={S.label}>Notes</label>
                    <input style={S.input} placeholder="Optional notes" value={ownerForm.notes} onChange={(e) => setOwnerForm({ ...ownerForm, notes: e.target.value })} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button style={{ ...S.btnPrimary, padding: "9px 20px", fontSize: 13 }} onClick={handleAddOwner}>Save Owner</button>
                  <button style={{ ...S.btnSecondary, padding: "9px 20px", fontSize: 13 }} onClick={() => setShowOwnerForm(false)}>Cancel</button>
                </div>
              </div>
            )}

            {/* Owners list */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px 100px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
              {["Owner", "Contact", "Property", "Mgmt Fee / mo", "Units"].map((h) => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>
            {owners.map((o) => {
              const prop  = properties.find((p) => p.id === o.propertyId);
              const units = renters.filter((u) => u.propertyId === o.propertyId).length;
              return (
                <div key={o.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 120px 100px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{o.name}</div>
                    {o.notes && <div style={{ fontSize: 11, color: "#5A5570", marginTop: 2 }}>{o.notes}</div>}
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>{o.email || "—"}</div>
                    <div style={{ fontSize: 12, color: "#9993A8" }}>{o.phone || ""}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>{prop?.address || prop?.name || "—"}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#22AA55" }}>{o.mgmtFee ? fmtC(o.mgmtFee) : "—"}</div>
                  <div style={{ fontSize: 13, color: "#9993A8" }}>{units} renters</div>
                </div>
              );
            })}
            {owners.length === 0 && <EmptyState icon="🏠" title="No owners added yet" subtitle="Add property owners to track management fees and contacts" />}
          </div>

          {/* Management fee summary */}
          {owners.length > 0 && (
            <div style={{ marginTop: 20, background: "#141418", border: "1px solid #ffffff08", borderRadius: 14, padding: "20px 24px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Monthly Management Revenue</div>
              <div style={{ display: "flex", gap: 24 }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#22AA55" }}>
                    {fmtC(owners.reduce((s, o) => s + (parseFloat(o.mgmtFee) || 0), 0))}
                  </div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>total monthly fees</div>
                </div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: "#E8622A" }}>
                    {fmtC(owners.reduce((s, o) => s + (parseFloat(o.mgmtFee) || 0), 0) * 12)}
                  </div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>annualized</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {subTab === "warnings" && (
        <div style={{ maxWidth: 700 }}>
          <div style={S.pageTitle}>Warning Letters</div>
          <div style={S.pageSub}>Generate formal warning letters for BC tenants</div>

          {/* Warning Type */}
          <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 14, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Warning Type</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
              {WARNING_TYPES.map((t) => (
                <button key={t}
                  style={{ padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1px solid ${warnForm.warningType === t ? "#FF2D2D" : "#ffffff15"}`, background: warnForm.warningType === t ? "#FF2D2D18" : "transparent", color: warnForm.warningType === t ? "#FF2D2D" : "#9993A8" }}
                  onClick={() => { setW("warningType", t); setW("bodyText", WARNING_BODY[t]||""); setW("requiredAction", WARNING_ACTION[t]||""); }}>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Tenant Info */}
          <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 14, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Tenant Information</div>

            {/* Step 1 - Property */}
            <div style={S.field}>
              <label style={S.label}>Property *</label>
              <select style={S.select} value={warnForm.propertyId} onChange={(e) => {
                const prop = properties.find((p) => String(p.id) === e.target.value);
                setW("propertyId", e.target.value);
                setW("property", prop ? (prop.address || prop.name) : "");
                setW("unit", "");
                setW("tenantName", "");
                setW("tenantEmail", "");
              }}>
                <option value="">-- Select Property --</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.address || p.name}</option>
                ))}
              </select>
            </div>

            {/* Step 2 - Unit (filtered by property) */}
            {warnForm.propertyId && (
              <div style={S.field}>
                <label style={S.label}>Unit *</label>
                <select style={S.select} value={warnForm.unit} onChange={(e) => {
                  setW("unit", e.target.value);
                  // Auto-populate tenant from profiles
                  const renter = users.find((u) => u.role === "renter" && String(u.propertyId) === String(warnForm.propertyId) && String(u.unit) === e.target.value);
                  if (renter) {
                    setW("tenantName", renter.name || "");
                    setW("tenantEmail", renter.email || "");
                  } else {
                    setW("tenantName", "");
                    setW("tenantEmail", "");
                  }
                }}>
                  <option value="">-- Select Unit --</option>
                  {Array.from({ length: properties.find((p) => String(p.id) === String(warnForm.propertyId))?.units || 1 }, (_, i) => (
                    <option key={i+1} value={String(i+1)}>Unit {i+1}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Step 3 - Tenant auto-populated or manual */}
            {warnForm.unit && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 8 }}>
                <div style={S.field}>
                  <label style={S.label}>Tenant Name *</label>
                  <input style={S.input} placeholder="e.g. John Smith" value={warnForm.tenantName} onChange={(e) => setW("tenantName", e.target.value)} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Tenant Email *</label>
                  <input style={S.input} type="email" placeholder="tenant@email.com" value={warnForm.tenantEmail} onChange={(e) => setW("tenantEmail", e.target.value)} />
                </div>
              </div>
            )}
            {warnForm.unit && !warnForm.tenantEmail && (
              <div style={{ fontSize: 11, color: "#FF7A22", marginBottom: 10 }}>
                No registered tenant found for this unit - enter email manually to send notice
              </div>
            )}
            {warnForm.unit && warnForm.tenantEmail && (
              <div style={{ fontSize: 11, color: "#22AA55", marginBottom: 10 }}>
                Tenant email auto-populated from portal registration
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={S.field}>
                <label style={S.label}>Date of Notice</label>
                <input style={S.input} type="date" value={warnForm.noticeDate} onChange={(e) => setW("noticeDate", e.target.value)} />
              </div>
              <div style={S.field}>
                <label style={S.label}>Incident Date</label>
                <input style={S.input} type="date" value={warnForm.incidentDate} onChange={(e) => setW("incidentDate", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Letter Body */}
          <div style={{ background: "#141418", border: "1px solid #ffffff08", borderRadius: 14, padding: 24, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#5A5570", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.06em" }}>Notice Body</div>
            <div style={S.field}>
              <label style={S.label}>Warning Text</label>
              <textarea style={{ ...S.input, minHeight: 100, resize: "vertical" }} value={warnForm.bodyText} onChange={(e) => setW("bodyText", e.target.value)} />
            </div>
            <div style={S.field}>
              <label style={S.label}>Required Action</label>
              <textarea style={{ ...S.input, minHeight: 80, resize: "vertical" }} value={warnForm.requiredAction} onChange={(e) => setW("requiredAction", e.target.value)} />
            </div>
          </div>

          {/* Signature */}
          <SignaturePanel
            sigName={warnForm.sigName}
            setSigName={(v) => setW("sigName", v)}
            canGenerate={!!warnForm.tenantName && !!warnForm.propertyId}
            noticeType={"Warning - " + warnForm.warningType}
            onGenerate={() => {
              const html = generateWarning(warnForm);
              addToHistory({ type:"Warning", tenantName: warnForm.tenantName, property: warnForm.property, unit: warnForm.unit, effectiveDate: warnForm.noticeDate, emailSent: false });
            }}
            onEmail={() => {
              const html = generateWarning(warnForm);
              sendNoticeEmail({ toEmail: warnForm.tenantEmail, toName: warnForm.tenantName, noticeType: "Warning - " + warnForm.warningType, property: warnForm.property, unit: warnForm.unit, effectiveDate: warnForm.noticeDate, landlordName: warnForm.landlordName, noticeHtml: html });
              addToHistory({ type:"Warning", tenantName: warnForm.tenantName, property: warnForm.property, unit: warnForm.unit, effectiveDate: warnForm.noticeDate, emailSent: true });
            }}
          />

          <div style={{ ...S.infoBanner, marginTop: 12 }}>
            <span>📋</span>
            <span style={{ fontSize: 12 }}>Warning letters are not official RTB forms but are important documentation. Keep a copy for your records. Three warnings strengthen a case for a Notice to End Tenancy.</span>
          </div>
        </div>
      )}

      {subTab === "history" && (
        <div style={{ maxWidth: 900 }}>
          <div style={S.tableWrap}>
            <div style={S.tableHeader}>
              <div style={S.tableTitle}>Document History</div>
              <div style={{ fontSize: 12, color: "#9993A8" }}>{history.length} documents generated</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "110px 100px 1fr 1fr 120px 110px 160px", gap: 12, padding: "10px 20px", background: "#0F0F14", borderBottom: "1px solid #ffffff08" }}>
              {["Generated", "Form", "Tenant", "Property / Unit", "Rent", "Emailed", "File"].map((h) => (
                <div key={h} style={{ fontSize: 11, fontWeight: 700, color: "#5A5570", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>
            {history.map((h) => {
              const isRTB7 = !h.type || h.type === "RTB-7";
              const badgeColor = isRTB7 ? "#22AA55" : h.type === "RTB-30" ? "#FF2D2D" : h.type === "RTB-32" ? "#FF7A22" : "#2A7BE8";
              return (
                <div key={h.id} style={{ display: "grid", gridTemplateColumns: "110px 100px 1fr 1fr 120px 90px 130px", gap: 12, padding: "14px 20px", borderBottom: "1px solid #ffffff06", alignItems: "center" }}>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>{formatDate(h.generatedOn)}</div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 99, background: badgeColor + "18", color: badgeColor }}>{h.type || "RTB-7"}</span>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{h.tenantName}</div>
                  <div style={{ fontSize: 12, color: "#9993A8" }}>{h.property} · Unit {h.unit}</div>
                  <div style={{ fontSize: 13, color: "#9993A8" }}>{isRTB7 ? (h.newRent > 0 ? fmtC(h.newRent) + "/mo" : "-") : "-"}</div>
                  <div style={{ fontSize: 11 }}>
                    {h.emailSent
                      ? <span style={{ color: "#22AA55", fontWeight: 600 }}>Sent</span>
                      : <span style={{ color: "#5A5570" }}>Not sent</span>}
                  </div>
                  <div>
                    {h.noticeUrl
                      ? <a href={h.noticeUrl} target="_blank" rel="noreferrer"
                          style={{ ...S.btnSmall, background: "#2A7BE818", color: "#2A7BE8", fontSize: 11, padding: "4px 10px", textDecoration: "none", borderRadius: 8 }}>
                          View
                        </a>
                      : <span style={{ fontSize: 11, color: "#5A5570" }}>No file</span>
                    }
                  </div>
                </div>
              );
            })}
            {history.length === 0 && <EmptyState icon="📄" title="No documents generated yet" subtitle="RTB-7, RTB-32, and Inspection reports will appear here" />}
          </div>
        </div>
      )}
    </div>
  );
};

export default RentIncreaseTab;
